import { cel, flame } from './motifs';
import { GOLD, M, RED } from './palette';
import { bez, type Pen, type Pt, spline } from './pen';
import type { Tones } from './registry';

/**
 * The plates' creatures, in units: a crane, a coiled dragon, a roaring white tiger and a
 * nine-tailed fox. Each is placed by a point and drawn facing right unless `flip`.
 */

/** Maps a creature's own coordinates (facing right) onto the plate. */
const placer =
  (x: number, y: number, k: number, flip: boolean) =>
  (u: number, v: number): Pt => [x + (flip ? -u : u) * k, y + v * k];

const CRANE_WHITE: Tones = ['#ffffff', '#ece8ea', '#b8b0bc'];

/** A red-crowned crane in flight, wings raised, its body's centre at (x, y), `k` its size (1 ≈ 24 units across). */
export function crane(p: Pen, x: number, y: number, k = 1, flip = false): void {
  const T = placer(x, y, k, flip);
  // Wings: the far one behind, then the near one, each a fan of feathers with black tips.
  const wing = (root: Pt, tip: Pt, back: Pt, tones: Tones) => {
    const fan: Pt[] = [T(...root), T(...tip)];
    for (let i = 0; i <= 4; i++) {
      const u = i / 4;
      const px = tip[0] + (back[0] - tip[0]) * u;
      const py = tip[1] + (back[1] - tip[1]) * u;
      fan.push(T(px + (i % 2 ? 1.2 : 0), py + (i % 2 ? 1 : 0)));
    }
    cel(p, tones, (q, c) => q.poly(fan, c));
    // Black primaries along the outer edge.
    p.taper(
      [T(...tip), T(tip[0] + (back[0] - tip[0]) * 0.45, tip[1] + (back[1] - tip[1]) * 0.45)],
      2.2 * k,
      1.2 * k,
      M.ink,
    );
  };
  wing([-1, -1], [-6, -14], [-11, -3], ['#e8e4ea', '#d0c8d4', '#a49cac']);
  // Body and tail.
  cel(p, CRANE_WHITE, (q, c) => {
    q.poly([T(-9, 1), T(-3, -2), T(5, -1.5), T(7, 0.5), T(1, 2.5), T(-6, 2.5)], c);
  });
  p.poly([T(-9, 1), T(-12, 0), T(-11, 2.5), T(-7, 2.5)], M.ink);
  // Legs trailing.
  p.line(...T(-5, 2), ...T(-14, 4.5), M.inkHi, Math.max(0.6, 0.6 * k));
  p.line(...T(-4, 2.2), ...T(-13.5, 5.5), M.inkHi, Math.max(0.6, 0.6 * k));
  // Neck, black, then the white cheek, red crown and beak.
  p.taper(spline([T(5, -1), T(9, -2.5), T(12, -2), T(13.5, -3)], 4), 1.6 * k, 1.1 * k, M.ink);
  p.disc(...T(13.8, -3.2), 1.1 * k, CRANE_WHITE[0]);
  p.px(...T(13.6, -4.4), M.red);
  p.line(...T(14.6, -3.2), ...T(17.5, -2.4), '#d8b060', Math.max(0.6, 0.7 * k));
  wing([0, -1], [3, -16], [-6, -2], CRANE_WHITE);
}

/**
 * A gold dragon coiling up through `path` (from its tail to its neck), its head at the last point
 * facing right (or left with `flip`), with a red mane and fins, scales and whiskers.
 */
export function dragon(p: Pen, path: readonly Pt[], w: number, flip = false, tones: Tones = GOLD): void {
  const body = spline(path, 8);
  // Fins along the back, in red.
  for (let i = 4; i < body.length - 4; i += 5) {
    const [a, b] = [body[i - 1]!, body[i + 1]!];
    const dx = b[0] - a[0];
    const dy = b[1] - a[1];
    const l = Math.hypot(dx, dy) || 1;
    const nx = (-dy / l) * (flip ? -1 : 1);
    const ny = (dx / l) * (flip ? -1 : 1);
    const at = body[i]!;
    const r = (w * (0.35 + (0.65 * i) / body.length)) / 2 + 1.5;
    p.poly(
      [
        [at[0] - dx * 0.6, at[1] - dy * 0.6],
        [at[0] + nx * r * 1.6, at[1] + ny * r * 1.6],
        [at[0] + dx * 0.6, at[1] + dy * 0.6],
      ],
      M.red,
    );
  }
  cel(p, tones, (q, c) => q.taper(body, w * 0.35, w, c), 1);
  // Belly scutes: a lighter stripe along the body, cut into plates.
  const belly = body.map(([x, y], i) => {
    const [a, b] = [body[Math.max(0, i - 1)]!, body[Math.min(body.length - 1, i + 1)]!];
    const dx = b[0] - a[0];
    const dy = b[1] - a[1];
    const l = Math.hypot(dx, dy) || 1;
    const r = (w * (0.35 + (0.65 * i) / body.length)) / 4;
    const s = flip ? 1 : -1;
    return [x + (dy / l) * r * s, y - (dx / l) * r * s] as Pt;
  });
  p.taper(belly, w * 0.12, w * 0.35, '#f6dc9a');
  for (let i = 3; i < body.length - 2; i += 3) {
    const [x, y] = body[i]!;
    p.px(x, y, tones[2]);
  }
  // Head at the end of the neck.
  const [hx, hy] = body[body.length - 1]!;
  const s = flip ? -1 : 1;
  const H = (u: number, v: number): Pt => [hx + u * s, hy + v];
  // Mane, then the head, the jaw and the snout.
  cel(p, RED, (q, c) => q.poly([H(-3, -2), H(-6, -5), H(-4, 0), H(-7, 1), H(-3, 3), H(-1, 1)], c));
  cel(p, tones, (q, c) => {
    q.poly([H(-2, -3), H(3, -3.5), H(7, -2), H(7.5, 0), H(4, 0.5), H(-2, 2.5)], c);
    q.poly([H(1, 1), H(6, 1.5), H(5, 3), H(0, 3)], c);
  });
  p.line(...H(2, 0.6), ...H(6.5, 1.1), M.redLo);
  p.px(...H(2, -2), M.ink).px(...H(3, -2.5), M.whiteHi);
  // Horns back over the mane, whiskers flowing.
  p.taper([H(0, -3), H(-3, -6), H(-6, -7)], 1.2, 0.5, '#f0e2c0');
  p.taper([H(1, -3), H(-1, -7), H(-4, -9)], 1.1, 0.5, '#f0e2c0');
  p.path(bez(H(7, 0), H(10, 2), H(9, 5), 5), tones[0]);
  p.path(bez(H(6.5, 2.5), H(8, 6), H(5, 8), 5), tones[0]);
}

/** A dragon's three-toed claw at (x, y), reaching right (or left). */
export function claw(p: Pen, x: number, y: number, flip = false, tones: Tones = GOLD): void {
  const s = flip ? -1 : 1;
  for (const [u, v] of [
    [2.5, -1.5],
    [3, 0],
    [2.5, 1.5],
  ] as const)
    p.taper(
      [
        [x, y],
        [x + u * s, y + v],
      ],
      1.4,
      0.6,
      tones[1],
    );
  p.px(x, y, tones[2]);
}

const TIGER: Tones = ['#ffffff', '#e6e4ec', '#a8a4b8'];

/** A white tiger rearing at (x, y) (the bottom of its chest), roaring to the right, `k` its size. */
export function tiger(p: Pen, x: number, y: number, k = 1, flip = false): void {
  const T = placer(x, y, k, flip);
  // Body and the far foreleg.
  cel(
    p,
    TIGER,
    (q, c) => {
      q.poly([T(-9, 2), T(-10, -8), T(-4, -14), T(3, -13), T(4, -4), T(1, 2)], c);
      q.taper([T(2, -6), T(7, -3), T(10, -1)], 3 * k, 2.4 * k, c);
    },
    1.5,
  );
  // Stripes on the body and the far leg: dark brush strokes, thick in the middle.
  for (const [a, b, cc] of [
    [T(-9.5, -5), T(-6, -5.5), T(-6.5, -2)],
    [T(-9, -9), T(-5.5, -9.5), T(-6, -6)],
    [T(-7, -12.5), T(-3.5, -12), T(-4.5, -9)],
    [T(-4, -4), T(-1, -3), T(-2, 0.5)],
    [T(0, -8), T(2.5, -8), T(2, -5)],
    [T(5, -4.5), T(6, -3), T(5.5, -1)],
    [T(7.5, -3), T(8.5, -1.5), T(8, 0.5)],
  ] as const)
    p.taper(bez(a, b, cc, 6), 1.3 * k, 0.4 * k, M.ink);
  // Near paw over the frame, with claws.
  cel(p, TIGER, (q, c) => q.taper([T(-1, -4), T(5, 1), T(9, 3)], 3.2 * k, 2.8 * k, c), 1);
  for (const u of [8, 9.5, 11]) p.px(...T(u, 4.6), M.whiteDeep);
  p.taper(bez(T(2, -1), T(4, 0), T(5, 2), 4), 1 * k, 0.4 * k, M.ink);
  // Head.
  cel(
    p,
    TIGER,
    (q, c) => {
      q.disc(...T(1, -15), 5.2 * k, c);
      q.disc(...T(-2.5, -20), 1.8 * k, c);
      q.disc(...T(3.5, -20.5), 1.8 * k, c);
    },
    1,
  );
  p.px(...T(-2.5, -20), '#e8a0b0').px(...T(3.5, -20.5), '#e8a0b0');
  // Muzzle and the roaring mouth.
  cel(p, TIGER, (q, c) => q.ell(...T(5, -12.5), 3.2 * k, 2.6 * k, c), 1);
  p.poly([T(3.5, -12), T(8.5, -12.5), T(8, -9.5), T(4, -9.5)], M.redLo);
  p.poly([T(4.5, -11.5), T(7.5, -11.5), T(7.2, -10), T(5, -10)], M.red);
  p.px(...T(5, -12), M.whiteHi)
    .px(...T(7.5, -12), M.whiteHi)
    .px(...T(5, -10), M.whiteHi);
  p.px(...T(7.5, -14.5), '#d07a8a');
  // Eyes and the stripes on the brow.
  p.px(...T(2.5, -16.5), M.goldHi).px(...T(3.2, -16.5), M.ink);
  p.taper(bez(T(-2, -17), T(0, -18.5), T(1.5, -17.5), 4), 0.9 * k, 0.4 * k, M.ink);
  p.taper(bez(T(-3, -14), T(-1.5, -15), T(-0.5, -13.5), 4), 0.9 * k, 0.4 * k, M.ink);
  p.line(...T(0.5, -19.5), ...T(1.5, -18.2), M.ink);
  p.taper(bez(T(-3.5, -12), T(-2.5, -11), T(-1, -11.5), 4), 0.9 * k, 0.4 * k, M.ink);
  p.px(...T(-2.5, -21), M.ink).px(...T(3.5, -21.5), M.ink);
}

const FOX: Tones = ['#d8b8ff', '#9a64e0', '#5a2e9a'];

/** A nine-tailed fox spirit sitting at (x, y) (its haunch), facing right, tails fanned behind it. */
export function fox(p: Pen, x: number, y: number, k = 1, flip = false, tones: Tones = FOX): void {
  const T = placer(x, y, k, flip);
  // Tails: plumes sweeping back off the haunch and curling up, each fat in the middle, tipped
  // white, the far ones darker (the nine read as a heap of plumes).
  const plumes: [number, number, number, number][] = [
    // root y, sweep back, rise, curl
    [-1, 15, 13, 5],
    [0, 13, 7, 6],
    [-2, 10, 17, 3],
    [1, 15, 2, 6],
    [-1, 6, 16, 6],
  ];
  plumes.forEach(([ry, back, rise, cur], i) => {
    const far = i % 2 === 0;
    const t: Tones = far ? [tones[1], tones[2], '#3a1a6a'] : tones;
    const curve = spline(
      [
        T(-1, ry - 2),
        T(-back * 0.5, ry - rise * 0.3 - 2),
        T(-back, ry - rise),
        T(-back + cur, ry - rise - 4),
      ],
      5,
    );
    const n = curve.length;
    cel(
      p,
      t,
      (q, c) => {
        for (let j = 0; j < n; j++) {
          const u = j / (n - 1);
          q.disc(curve[j]![0], curve[j]![1], (1.2 + Math.sin(u * Math.PI) * 2.4) * k, c);
        }
      },
      1,
    );
    const [ex, ey] = curve[n - 1]!;
    p.disc(ex, ey, 1.4 * k, '#f4ecff');
  });
  // Body, chest and head.
  cel(
    p,
    tones,
    (q, c) => {
      q.ell(...T(1, -4), 4 * k, 4.8 * k, c);
      q.poly([T(1, -9), T(4, -14), T(9, -11), T(10, -9), T(5, -7)], c);
      q.poly([T(3, -13), T(3.5, -18), T(6, -13.5)], c);
      q.poly([T(5.5, -13), T(7.5, -17), T(8, -12)], c);
    },
    1,
  );
  p.ell(...T(3.5, -4), 1.8 * k, 3.2 * k, '#f0e4ff');
  p.px(...T(6.5, -11.5), M.goldHi).px(...T(9.8, -9.4), M.ink);
  p.px(...T(3.8, -15.5), '#f4a0c8');
  // Foxfire at its feet.
  flame(p, x + (flip ? -9 : 9) * k, y + 1, 7 * k, 4 * k, flip ? -1 : 1, [tones[2], tones[1], '#f4ecff']);
}
